/**
 * Checkout publico (spec 003, 4.1), no modelo do checkout do legado que o Vitor prefere (15/09: "pegue ele como base
 * mas aplique a nova identidade e as novas regras"): cabecalho centralizado com o nome do checkout, "Sua compra" ao lado
 * e tudo numa pagina so (resumo com o total, PIX ou cartao, dados do comprador, regulamento e o botao de pagar). A
 * plataforma, quando o checkout deixa escolher, e perguntada numa janela na abertura, como no legado.
 *
 * Regras novas: valor, parcelas e situacao vem do servico de pagamentos (a pagina nunca calcula); o botao abre a
 * tentativa e paga em seguida; a sessao fica no link (`?s=`), refresh volta pro mesmo ponto; o cartao e tokenizado
 * direto na Pagar.me; regulamento versionado e politica de privacidade.
 *
 * Comprar logado (spec 045): sem sessao, a pagina mostra o plano, o preco, a plataforma, os beneficios e o cupom, e no
 * lugar dos dados e do pagamento pede "Entrar" ou "Criar conta", ali mesmo. Logado, nome, CPF e e-mail vem da conta,
 * travados, e a tentativa vai com o token da area: o servico pega a pessoa da sessao. O voltar (botao e navegador, quando
 * o link foi a primeira pagina) leva pro back redirect do checkout ou, sem ele, pro site da marca.
 */
import { dolarDoReal, voltarParaValido, type Cotacao, type EstadoDaTentativa, type NovaTentativa, type PromocaoNaVitrine, type Vitrine } from "@mtc/contratos";
import {
  AceiteDosDocumentos, Aviso, Botao, BotaoLink, Campo, CampoCaixa, CartaoSuaCompra, CascaDoCheckout, Carregando, CompraConfirmada, CompradorDaConta, ContaNaCompra, DestaqueDaPromocao, EscolhaDeMeio, IconeCartao, IconeCarteira, IconeEmail, IconeEscudo, IconePix, IconeSeta,
  JanelaDePlataforma, PASSOS_DO_PLANO, PagamentoPix, Pilha, ResumoDoPedido, SecaoDoCheckout, SeloDaPromocao, SeloSemAssinatura, formatarDolares, formatarReais, formatarTelefone,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useParams, useSearchParams } from "react-router";
import { config, marca } from "../config";
import { paginaDoRegulamento } from "../regulamento";
import { useSessao } from "../sessao";
import { useVoltarProSite } from "../voltar";
import { useContaNaCompra } from "./Acesso";
import { contarNoCheckout, origemDaVisita, visitante } from "./analise";
import { usePixelDaCompra } from "./pixel";
import { descontoAnunciado } from "./promocao";
import { CamposDoCartao, CARTAO_VAZIO, cartaoPronto, chamar, chamarComSessao, corpoDoCartao, Instabilidade, parcelaEscolhida, type DadosDoCartao } from "./pagamento";

/**
 * Vitrine em dolar (Vitor, 15/10: "preciso que mostre o valor em dolar nos lugares de valores em Reais"): todo valor
 * que a pagina mostra sai em dolar, pela cotacao da vitrine (`dolarDoReal`, a conta da 020 ao contrario, que devolve o
 * preco anunciado exato). O real, que e o que se cobra, aparece na linha da cotacao embaixo do preco e do total.
 * Vitrine em real (Prop): tudo como antes.
 */
export function dinheiroDa(v: Pick<Vitrine, "anunciado_em_dolar">) {
  const d = v.anunciado_em_dolar;
  return {
    valor: (brl: number) => (d ? formatarDolares(dolarDoReal(brl, d.cotacao)) : formatarReais(brl)),
    /** O par do destaque: o dolar deste valor e a cotacao, pro cartao "Sua compra" e o total do resumo. */
    emDolar: (brl: number) => (d ? { usdCentavos: dolarDoReal(brl, d.cotacao), cotacao: d.cotacao, cotacaoEm: d.cotacao_em } : null),
  };
}

type Plataforma = "PROFIT_ONE" | "PROFIT_PRO";
type Meio = "pix" | "cartao";
/**
 * O que o comprador preenche. Fica na pagina: abrir a tentativa troca o que aparece, mas nao apaga o que foi digitado, e
 * entrar ou criar a conta no meio do caminho tambem nao. `telefone` nulo: vale o celular da conta.
 */
type Preenchido = { meio: Meio | null; telefone: string | null; aceite: boolean; aceites: string[]; cartao: DadosDoCartao; beneficios: string[] };
const PREENCHIDO_VAZIO: Preenchido = { meio: null, telefone: null, aceite: false, aceites: [], cartao: CARTAO_VAZIO, beneficios: [] };
/** Quem esta comprando, pela sessao da area (spec 045). Nome, CPF e e-mail vem da conta e nao se trocam no checkout. */
type Comprador =
  | { estado: "carregando" }
  | { estado: "anonimo" }
  | { estado: "logado"; nome: string; cpf: string; email: string; telefone: string | null; assistido: boolean };
const NOME_DA_PLATAFORMA: Record<Plataforma, string> = { PROFIT_ONE: "Profit One", PROFIT_PRO: "Profit Pro" };
const digitos = (v: string) => v.replace(/\D/g, "");
const buscarTentativa = (sessao: string) => chamar<EstadoDaTentativa>(`/v1/tentativas/${encodeURIComponent(sessao)}`);

/**
 * O regulamento que esta compra aceita (spec 042, 3.4): a versao que esta no ar AGORA no nucleo e os documentos dela (o
 * geral e o da familia do plano), uma caixa por documento. Nada publicado ainda no nucleo (404) ou nucleo fora do ar: a
 * caixa unica de antes, com o regulamento da marca (o checkout nao depende do nucleo pra vender, 017, decisao 12).
 * Familia sem regulamento publicado (409): nao vende, em vez de mostrar o documento de outra (017, decisao 7).
 */
type RegulamentoDaPagina =
  | { estado: "carregando" }
  | { estado: "nucleo"; grupo: number; documentos: { chave: string; titulo: string; href: string }[] }
  | { estado: "marca" }
  | { estado: "sem-regulamento" };
function useRegulamentoDaCompra(ligado: boolean, planoSlug: string | undefined): RegulamentoDaPagina {
  const q = useQuery({
    enabled: ligado,
    queryKey: ["regulamento-para-compra", planoSlug ?? ""],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<RegulamentoDaPagina> => {
      const api = config().API_URL.replace(/\/$/, "");
      try {
        const r = await fetch(`${api}/v1/publico/regulamentos/para-compra${planoSlug ? `?plano=${encodeURIComponent(planoSlug)}` : ""}`);
        if (r.status === 409) return { estado: "sem-regulamento" };
        if (!r.ok) return { estado: "marca" };
        const j = (await r.json()) as { grupo: number; documentos: { chave: string; titulo: string; url: string }[] };
        // Cada caixa abre o documento formatado pra ler (pedido do Vitor, 13/10), nao o arquivo cru.
        return { estado: "nucleo", grupo: j.grupo, documentos: j.documentos.map((d) => ({ chave: d.chave, titulo: d.titulo, href: paginaDoRegulamento(d.url) })) };
      } catch {
        return { estado: "marca" };
      }
    },
  });
  return q.data ?? { estado: "carregando" };
}

/** Contratos do plano, da vitrine publica do backend (a do checkout nao traz): so pra mostrar em "Sua compra". */
function useContratos(planoSlug: string | undefined) {
  const q = useQuery({
    // Sem plano (checkout "Sem assinatura", spec 036) nao ha contrato pra mostrar: nem pergunta.
    enabled: Boolean(planoSlug),
    queryKey: ["planos-publicos"],
    queryFn: async () => {
      const r = await fetch(`${config().API_URL.replace(/\/$/, "")}/v1/planos`);
      return r.ok ? ((await r.json()) as { itens: { slug: string; contratos: number | null }[] }).itens : [];
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
  const c = planoSlug ? q.data?.find((p) => p.slug === planoSlug)?.contratos : null;
  return c ? `${c} ${c === 1 ? "contrato" : "contratos"}` : null;
}

/**
 * Link de checkout do sistema antigo (spec 009, T12): o token velho ainda circula em e-mail e WhatsApp. O servico diz
 * pra onde ele foi e a pessoa segue sozinha, sem entender de mudanca de sistema nenhuma.
 */
function LevandoPraOEnderecoNovo({ destino }: { destino: string }) {
  useEffect(() => {
    const t = setTimeout(() => { window.location.replace(destino); }, 1200);
    return () => clearTimeout(t);
  }, [destino]);
  return (
    <div className="mtc-cartao">
      <Pilha espaco={3}>
        <Aviso tipo="alerta">Este link mudou de lugar. Estamos te levando pro endereço novo.</Aviso>
        <a className="mtc-link" href={destino}>Se nada acontecer em alguns segundos, toque aqui.</a>
      </Pilha>
    </div>
  );
}

export function PaginaCheckout() {
  const { token = "" } = useParams();
  const [busca, setBusca] = useSearchParams();
  const sessao = busca.get("s") ?? "";
  const qc = useQueryClient();
  const m = marca();
  const conta = useSessao();
  const contar = (nome: Parameters<typeof contarNoCheckout>[1], dados?: Parameters<typeof contarNoCheckout>[2]) => contarNoCheckout(token, nome, dados);
  // Analise (spec 062): entrar, criar a conta e os erros dessas telas viram passos da visita. O resultado nao muda.
  const acoes = useContaNaCompra();
  const acoesDaConta: typeof acoes = {
    ...acoes,
    aoEntrar: async (d) => {
      const e = await acoes.aoEntrar(d);
      contar(e ? "erro" : "entrou", e ? { etapa: "entrar", mensagem: e } : {});
      return e;
    },
    aoIniciarCadastro: async (d) => {
      const r = await acoes.aoIniciarCadastro(d);
      if (r.tipo !== "enviado") contar("erro", { etapa: "cadastro", mensagem: r.tipo === "erro" ? r.erro : r.tipo === "cpf-tem-conta" ? "Este CPF já tem conta" : "Este e-mail já tem conta" });
      return r;
    },
    aoConfirmarCadastro: async (d) => {
      const e = await acoes.aoConfirmarCadastro(d);
      contar(e ? "erro" : "criou_conta", e ? { etapa: "cadastro", mensagem: e } : {});
      return e;
    },
    aoCriarSenha: async (d) => {
      const e = await acoes.aoCriarSenha(d);
      contar(e ? "erro" : "entrou", e ? { etapa: "senha", mensagem: e } : { com: "nova-senha" });
      return e;
    },
  };
  const [avisoDaConta, setAvisoDaConta] = useState<string | null>(null);
  const [saindo, setSaindo] = useState(false);
  const u = conta.estado.status === "logado" ? conta.estado.usuario : null;
  const comprador: Comprador = conta.estado.status === "carregando" ? { estado: "carregando" } : !u ? { estado: "anonimo" } : {
    estado: "logado",
    nome: u.nome,
    cpf: u.cpf,
    email: (u.emails.find((e) => e.principal) ?? u.emails[0])?.email ?? "",
    telefone: u.telefone ?? null,
    assistido: Boolean(u.assistido),
  };
  // Entrou (ou a conta nasceu): o recado de sessao vencida sai.
  useEffect(() => { if (u) setAvisoDaConta(null); }, [u]);
  // A promocao de CPF (spec 067, 2.5) so vem com o login da area: logado, a vitrine e a cotacao vao com o token. Enquanto
  // a sessao e conferida a pagina ja mostra a vitrine de todo mundo; quando a conta entra, busca de novo sem piscar.
  const quemCompra = u?.cpf ?? "";
  const doServico = <T,>(caminho: string, init?: RequestInit) => (u ? chamarComSessao<T>(conta.api, caminho, init) : chamar<T>(caminho, init));
  const vitrine = useQuery({ queryKey: ["checkout", token, quemCompra], queryFn: () => doServico<Vitrine>(`/v1/checkouts/${encodeURIComponent(token)}`), retry: false, staleTime: 60_000, placeholderData: (anterior) => anterior });
  // O voltar (botao e navegador, com o link aberto direto) vai pro back redirect do checkout, quando o administrador
  // definiu um (spec 045, 3.10); sem ele, pro site da marca (3.7). Enquanto a vitrine carrega o destino nao e conhecido:
  // nem o botao aparece nem o historico e marcado. O endereco vira link, entao passa pela mesma regra do servico.
  const doCheckout = vitrine.data?.ok ? vitrine.data.dados.voltar_para : null;
  const voltarPara = vitrine.isLoading ? undefined : doCheckout && voltarParaValido(doCheckout) ? doCheckout : m.linkPlanos;
  useVoltarProSite(voltarPara);
  const estado = useQuery({
    queryKey: ["tentativa", sessao],
    queryFn: () => buscarTentativa(sessao),
    enabled: Boolean(sessao),
    retry: false,
    // Enquanto o PIX aguarda, pergunta a cada 3 s: a confirmacao aparece sozinha.
    refetchInterval: (q) => (q.state.data?.ok && q.state.data.dados.pedido?.status === "AGUARDANDO" ? 3_000 : false),
  });

  const v = vitrine.data?.ok ? vitrine.data.dados : null;
  // Checkout "Sem assinatura" (spec 036): so cobra. Nao fala de plano, contrato nem mensalidade, e nem pergunta por eles.
  const semAssinatura = v?.sem_assinatura ?? false;
  const planoSlug = v && !v.sem_assinatura ? (v.plano_slug ?? undefined) : undefined;
  const contratos = useContratos(planoSlug);
  const regulamento = useRegulamentoDaCompra(Boolean(v?.termos), planoSlug);

  // Escolha de plataforma (janela da abertura, como no legado). Com a tentativa aberta, vale a dela.
  const [plataforma, setPlataforma] = useState<Plataforma | null>(null);
  const [marcada, setMarcada] = useState<Plataforma | null>(null);
  const [janela, setJanela] = useState(false);
  const [preenchido, setPreenchido] = useState<Preenchido>(PREENCHIDO_VAZIO);
  // Cupom (spec 016): antes da tentativa vale o que a pessoa aplicou aqui; com tentativa, o que o servidor guardou nela.
  const [cupomAplicado, setCupomAplicado] = useState("");
  const tentativa = sessao && estado.data?.ok ? estado.data.dados : null;
  // Compra paga com pixel ligado no ambiente: a Meta recebe do navegador tambem, com o mesmo id (spec 013, item 4).
  usePixelDaCompra(tentativa?.pedido?.rastreio);
  const escolhida: Plataforma | null = tentativa?.checkout?.plataforma ?? plataforma;
  const opcoes = v?.opcoes_de_plataforma ?? null;
  useEffect(() => {
    if (opcoes && !sessao && !plataforma) setJanela(true);
  }, [opcoes, sessao, plataforma]);
  const opcao = opcoes && escolhida ? (opcoes.find((o) => o.plataforma === escolhida) ?? null) : null;
  // Beneficios (spec 013, item 2): antes da tentativa, os que a pessoa marcou; depois, os que ficaram na tentativa.
  const beneficiosEscolhidos = tentativa?.checkout ? tentativa.checkout.beneficios.map((b) => b.id) : preenchido.beneficios;
  // Com beneficio marcado, preco e parcelas vem da cotacao do servidor (a pagina nunca soma).
  const cupom = tentativa ? (tentativa.cupom?.codigo ?? "") : cupomAplicado;
  const precisaCotar = beneficiosEscolhidos.length > 0 || Boolean(cupom);
  const cotacao = useQuery({
    queryKey: ["cotacao", token, escolhida, beneficiosEscolhidos.join(","), cupom, quemCompra],
    queryFn: () => doServico<Cotacao>(`/v1/checkouts/${encodeURIComponent(token)}/cotacao`, { method: "POST", body: JSON.stringify({ ...(escolhida && opcoes ? { plataforma: escolhida } : {}), beneficios: beneficiosEscolhidos, ...(cupom ? { cupom } : {}) }) }),
    enabled: Boolean(v) && precisaCotar,
    placeholderData: (anterior) => anterior,
  });
  const cotada = precisaCotar && cotacao.data?.ok ? cotacao.data.dados : null;
  // Cupom que a pagina aplicou mas o servidor recusou (esgotou no meio do caminho): sai do resumo e avisa.
  const cupomErro = cotada?.cupom_erro ?? null;
  const descontoDoCupom = tentativa?.cupom?.desconto_centavos ?? cotada?.cupom?.desconto_centavos ?? 0;
  // Promocao (spec 067): com cotacao (beneficio ou cupom), a que a cotacao diz (nunca junto com o cupom: vale o maior);
  // sem ela, a da plataforma escolhida ou a do preco base. Com promocao, as parcelas da vitrine ja sao do preco promocional.
  const daVitrine: PromocaoNaVitrine | null = (opcao ? opcao.promocao : v?.promocao) ?? null;
  const promocao = precisaCotar ? (cotada?.promocao ?? null) : daVitrine;
  // A oferta desta compra: a cotacao (com beneficio), a da plataforma escolhida ou a do checkout, com a promocao quando ha;
  // sempre do servidor (a pagina nunca subtrai).
  const daPlataforma = v ? (opcao ? { ...v, preco_centavos: opcao.preco_centavos, parcelas: opcao.parcelas } : v) : null;
  const comPromocao = daPlataforma && !precisaCotar && promocao ? { ...daPlataforma, preco_centavos: promocao.preco_centavos } : daPlataforma;
  const oferta = comPromocao && cotada ? { ...comPromocao, preco_centavos: cotada.preco_centavos, parcelas: cotada.parcelas } : comPromocao;
  // Cotacao que falhou nunca vira botao morto (G06: 15 horas sem pedido no legado porque a cotacao dava 400 e ninguem
  // via). A tela diz o que houve e deixa tentar de novo ou seguir sem os beneficios.
  const falhaNaCotacao = precisaCotar && !cotacao.isFetching && !cotada
    ? cotacao.data && !cotacao.data.ok ? { erro: cotacao.data.fora ? null : cotacao.data.erro, fora: cotacao.data.fora } : cotacao.isError ? { erro: null, fora: true } : null
    : null;
  const cotando = precisaCotar && (cotacao.isFetching || (!cotada && !falhaNaCotacao));

  // Analise (spec 062). Abriu: uma vez por carga, quando a vitrine e a sessao ja sao conhecidas (o refresh com `?s=` e a
  // mesma visita e nao conta de novo). Sem conta, a pagina pede pra entrar: e a parede do login da spec 045.
  const abriu = useRef(false);
  const pediuConta = useRef(false);
  useEffect(() => {
    if (abriu.current || !v || comprador.estado === "carregando" || sessao) return;
    abriu.current = true;
    contar("aberto", { logado: comprador.estado === "logado" });
  }, [v, comprador.estado, sessao]);
  useEffect(() => {
    if (!abriu.current || pediuConta.current || sessao || comprador.estado !== "anonimo") return;
    pediuConta.current = true;
    contar("conta_pedida");
  }, [v, comprador.estado, sessao]);
  // Cupom antes da tentativa: quem diz se serve e a cotacao. Com a tentativa, quem conta e o `aoAplicarCupom`.
  const cupomContado = useRef("");
  useEffect(() => {
    if (sessao || !cupom || cotacao.isFetching) return;
    const chave = `${cupom}:${cupomErro ?? "ok"}`;
    if (cupomContado.current === chave || (!cupomErro && !cotada?.cupom)) return;
    cupomContado.current = chave;
    contar("cupom", cupomErro ? { resultado: "recusado", motivo: cupomErro } : { resultado: "aplicado" });
  }, [sessao, cupom, cupomErro, cotada, cotacao.isFetching]);
  // Saiu: fechou, voltou ou escondeu a aba. Vai o passo em que estava e quanto tempo ficou (a analise usa o ultimo).
  const passo = useRef("conta");
  passo.current = tentativa?.pedido?.status === "PAGO" ? "pago" : sessao ? "pagamento" : comprador.estado === "logado" ? "dados" : "conta";
  useEffect(() => {
    const inicio = Date.now();
    let vezes = 0;
    const saiu = () => {
      if (!abriu.current || vezes >= 5) return;
      vezes++;
      contar("saiu", { passo: passo.current, segundos: Math.round((Date.now() - inicio) / 1000) });
    };
    const aoEsconder = () => { if (document.visibilityState === "hidden") saiu(); };
    window.addEventListener("pagehide", saiu);
    document.addEventListener("visibilitychange", aoEsconder);
    return () => {
      window.removeEventListener("pagehide", saiu);
      document.removeEventListener("visibilitychange", aoEsconder);
    };
  }, [token]);

  const casca = (conteudo: React.ReactNode, lado?: React.ReactNode, faixa?: React.ReactNode) => (
    <CascaDoCheckout marca={m} titulo={v?.nome} voltarHref={voltarPara} lado={lado} faixa={faixa}>
      {conteudo}
    </CascaDoCheckout>
  );

  if (vitrine.isLoading) return casca(<Carregando compacto texto="Carregando o pagamento" />);
  if (!v || !oferta) {
    const r = vitrine.data;
    // Link antigo que mudou de lugar (spec 009, T12): leva pro endereco novo em vez de mostrar erro.
    if (r && !r.ok && r.mudouPara) return casca(<LevandoPraOEnderecoNovo destino={r.mudouPara} />);
    return casca(
      <div className="mtc-cartao">
        {r && !r.ok && r.inexistente ? (
          <Aviso tipo="erro">Este link de pagamento não existe mais. {m.linkPlanos ? <a href={m.linkPlanos}>Veja os planos disponíveis</a> : null}</Aviso>
        ) : r && !r.ok && !r.fora ? (
          <Aviso tipo="erro">{r.erro}</Aviso>
        ) : (
          <Instabilidade />
        )}
      </div>,
    );
  }

  const base = v.preco_centavos;
  const dinheiro = dinheiroDa(v);
  const detalheDaPlataforma = (o: { preco_centavos: number }) => (o.preco_centavos > base ? `+ ${dinheiro.valor(o.preco_centavos - base)}` : "Incluso");
  const pedido = tentativa?.pedido ?? null;
  // Depois de pagar (ou de abrir a tentativa), busca a situacao ja com o pedido e so entao poe a sessao no link: assim a
  // pagina nao passa pelo "carregando" no meio do envio (o formulario sumia com o PIX ainda saindo e ficava preso).
  const concluir = async (s: string) => {
    await qc.fetchQuery({ queryKey: ["tentativa", s], queryFn: () => buscarTentativa(s), staleTime: 0 });
    if (s !== sessao) setBusca({ s }, { replace: true });
  };
  // "Nao e voce? Sair" (spec 045, 3.5): desloga e volta pro "Entrar ou criar conta" NESTA pagina, sem perder as escolhas.
  const sair = async () => {
    setSaindo(true);
    try { await conta.sair(); } finally { setSaindo(false); }
    setPreenchido((p) => ({ ...p, telefone: null }));
  };
  // A sessao venceu no meio da compra (o servico respondeu 401): volta pro entrar, com as escolhas e o recado.
  const perdeuASessao = async () => {
    await conta.sair();
    setPreenchido((p) => ({ ...p, telefone: null }));
    setAvisoDaConta("Sua sessão venceu. Entre de novo pra comprar.");
  };
  // O preco mudou (o pagamento recusou, ou a contagem da promocao chegou a zero): vitrine, cotacao e tentativa de novo.
  const atualizarPreco = () => {
    void vitrine.refetch();
    if (precisaCotar) void cotacao.refetch();
    if (sessao) void estado.refetch();
  };
  const trocarPlataforma = () => {
    setMarcada(escolhida);
    // Com a tentativa aberta, outra plataforma e outra tentativa (outro preco): volta pro comeco, sem perder o digitado.
    if (sessao) setBusca({}, { replace: true });
    setJanela(true);
  };

  // O cartao da esquerda segue o mesmo valor do resumo: plataforma, beneficios e cupom ja entram no preco do servidor
  // (Vitor, 18/09: "o valor do lado tambem tem que se adaptar"). Sem escolha de plataforma feita, ainda e o plano base.
  const faltaEscolherPlataforma = Boolean(opcoes) && !escolhida;
  const totalDaCompra = oferta?.preco_centavos ?? base;
  // Com promocao, o cartao mostra "de / por": o cheio (o subtotal da cotacao ou o preco da plataforma) riscado e o
  // promocional em destaque. Sem a plataforma escolhida, o plano base com a promocao dele.
  const precoDoCartao = faltaEscolherPlataforma ? (promocao?.preco_centavos ?? base) : totalDaCompra;
  const cheioDoCartao = faltaEscolherPlataforma ? base : (cotada?.subtotal_centavos ?? daPlataforma?.preco_centavos ?? base);
  const precoDe = promocao && cheioDoCartao > precoDoCartao ? dinheiro.valor(cheioDoCartao) : undefined;
  // Sem "Plataforma X incluída" nem o texto da mensalidade (Vitor, 07/10: "preciso que remova isso dos checkouts"): a
  // plataforma escolhida ja aparece na escolha, e a mensalidade e conversa da area, nao da compra.
  const notaDoCartao = [
    faltaEscolherPlataforma ? "O valor da plataforma é somado conforme a sua escolha." : null,
    !faltaEscolherPlataforma && cupom && descontoDoCupom > 0 ? `Cupom ${cupom}: menos ${dinheiro.valor(descontoDoCupom)}.` : null,
  ].filter(Boolean).join(" ");
  const lado = (
    <CartaoSuaCompra
      nome={v.nome}
      detalhe={contratos}
      etiqueta={semAssinatura ? <SeloSemAssinatura /> : promocao ? <SeloDaPromocao nome={promocao.nome} tema={promocao.tema} desconto={descontoAnunciado(promocao, v)} /> : undefined}
      rotuloPreco={faltaEscolherPlataforma ? "Plano base" : totalDaCompra !== base ? "Total" : "Preço"}
      precoCentavos={precoDoCartao}
      precoDe={precoDe}
      // Vitrine em dolar (15/10): o dolar em destaque e, embaixo, quanto sai em real pela cotacao. Com beneficio, juros
      // ou cupom, o dolar e o do total (a conta ao contrario); sem nada, volta exato o preco anunciado.
      emDolar={dinheiro.emDolar(precoDoCartao)}
      nota={notaDoCartao || null}
    />
  );

  let principal: React.ReactNode;
  if (sessao && estado.isLoading) principal = <Carregando compacto texto="Conferindo o pagamento" />;
  else if (sessao && estado.data && !estado.data.ok) {
    principal = estado.data.status === 404 || estado.data.status === 410 ? (
      <Pilha espaco={3}>
        <Aviso tipo="alerta">{estado.data.erro || "Esta sessão de pagamento expirou."}</Aviso>
        <Botao onClick={() => setBusca({}, { replace: true })}>Começar de novo</Botao>
      </Pilha>
    ) : <Instabilidade />;
  } else if (pedido?.status === "PAGO") {
    // O botao leva pro login com `compra=1`: sem sessao, o login explica o primeiro acesso; com a sessao de alguem aberta
    // no aparelho, a area pergunta antes de entrar (nunca cai direto na conta de outra pessoa).
    const email = tentativa?.email_mascarado ? <strong className="mtc-confirmada__email">{tentativa.email_mascarado}</strong> : null;
    // Comprou logado (spec 045): a conta ja existe e esta aberta neste aparelho, entao o botao vai direto pra area e nao
    // fala de primeiro acesso. Sem sessao (o link `?s=` aberto em outro aparelho), segue o caminho do login.
    // Enquanto a sessao e conferida (refresh no link), o texto ja e o de quem comprou logado e o botao espera.
    const naConta = conta.estado.status !== "anonimo";
    const irPraArea = conta.estado.status === "logado"
      ? <BotaoLink href="/" variante="primario">Ir para a área do cliente <IconeSeta tamanho={16} /></BotaoLink>
      : naConta ? <Carregando compacto texto="Conferindo sua sessão" /> : null;
    principal = semAssinatura ? (
      // So cobra (spec 036): nada de "plano garantido" nem "agende o inicio", que prometeriam o que nao acontece.
      <CompraConfirmada
        nome={v.nome}
        texto={<>Recebemos o seu pagamento de <strong>{v.nome}</strong>. O comprovante foi {email ? <>para {email}</> : "pro seu e-mail"}.</>}
        passos={[
          { icone: <IconeEmail tamanho={18} />, titulo: "Guarde o comprovante", texto: "Ele está no e-mail que enviamos. Não achou? Olhe o spam e as promoções." },
          { icone: <IconeCarteira tamanho={18} />, titulo: "Pagamento avulso", texto: "Este pagamento não inclui plano novo. Se ele faz parte de um atendimento, a equipe segue com você por lá." },
        ]}
        linhas={[
          { rotulo: "Tipo", valor: <SeloSemAssinatura /> },
          { rotulo: "Pago com", valor: pedido.meio === "pix" ? "PIX" : pedido.parcelas > 1 ? `Cartão em ${pedido.parcelas}x` : "Cartão à vista" },
          // Em dolar, o real cobrado vai junto: e o que aparece no extrato e no comprovante.
          { rotulo: "Total", valor: v.anunciado_em_dolar ? `${dinheiro.valor(pedido.valor_centavos)} (${formatarReais(pedido.valor_centavos)})` : formatarReais(pedido.valor_centavos) },
        ]}
        acao={irPraArea ?? <BotaoLink href="/entrar?compra=1" variante="primario">Entrar na área <IconeSeta tamanho={16} /></BotaoLink>}
        ajuda={naConta ? undefined : <>O e-mail está errado ou não chegou? <a href="/suporte/email">Troque o e-mail aqui</a>.</>}
      />
    ) : (
      <CompraConfirmada
        nome={v.nome}
        emailMascarado={tentativa?.email_mascarado}
        texto={naConta ? <>Seu plano <strong>{v.nome}</strong> está garantido e já está na sua conta. O comprovante foi {email ? <>para {email}</> : "pro seu e-mail"}.</> : undefined}
        passos={naConta ? [{ icone: <IconeEmail tamanho={18} />, titulo: "Guarde o comprovante", texto: "Ele está no e-mail que enviamos. Não achou? Olhe o spam e as promoções." }, ...PASSOS_DO_PLANO.slice(1)] : undefined}
        linhas={[
          ...(escolhida ? [{ rotulo: "Plataforma", valor: NOME_DA_PLATAFORMA[escolhida] }] : []),
          ...(tentativa?.promocao ? [{ rotulo: "Promoção", valor: tentativa.promocao.nome }] : []),
          { rotulo: "Pago com", valor: pedido.meio === "pix" ? "PIX" : pedido.parcelas > 1 ? `Cartão em ${pedido.parcelas}x` : "Cartão à vista" },
          // Em dolar, o real cobrado vai junto: e o que aparece no extrato e no comprovante.
          { rotulo: "Total", valor: v.anunciado_em_dolar ? `${dinheiro.valor(pedido.valor_centavos)} (${formatarReais(pedido.valor_centavos)})` : formatarReais(pedido.valor_centavos) },
        ]}
        acao={irPraArea ?? <BotaoLink href="/entrar?compra=1" variante="primario">Entrar na área do trader <IconeSeta tamanho={16} /></BotaoLink>}
        ajuda={naConta ? undefined : <>O e-mail está errado ou não chegou? <a href="/suporte/email">Troque o e-mail aqui</a>.</>}
      />
    );
  } else if (pedido?.status === "AGUARDANDO" && pedido.pix) {
    principal = <PagamentoPix qrCodeUrl={pedido.pix.qr_code_url} copiaECola={pedido.pix.qr_code} expiraEm={pedido.pix.expira_em} />;
  } else if (pedido?.status === "AGUARDANDO") {
    principal = <Carregando compacto texto="Processando o pagamento" />;
  } else {
    principal = (
      <Formulario
        vitrine={v}
        oferta={oferta}
        sessao={sessao}
        regulamento={regulamento}
        plataforma={escolhida}
        precisaDePlataforma={Boolean(opcoes)}
        aviso={pedido?.status === "RECUSADO" ? (pedido.recusa ?? "Pagamento recusado.") : pedido?.status === "EXPIRADO" ? "O PIX anterior expirou. Gere outro abaixo." : null}
        linhaDaPlataforma={opcao ? { rotulo: NOME_DA_PLATAFORMA[opcao.plataforma], valor: detalheDaPlataforma(opcao) } : null}
        aoTrocarPlataforma={opcoes ? trocarPlataforma : undefined}
        preenchido={preenchido}
        aoPreencher={setPreenchido}
        escolhidos={beneficiosEscolhidos}
        cupom={cupom}
        cupomDesconto={descontoDoCupom}
        cupomErro={cupomErro}
        promocao={promocao}
        cupomEmDestaque={v.cupom_em_destaque}
        aoAplicarCupom={async (codigo) => {
          // Sem tentativa ainda, o codigo entra na cotacao e vai junto quando ela for aberta; com tentativa, reserva agora.
          if (!sessao) { setCupomAplicado(codigo); return null; }
          const r = await chamar(`/v1/tentativas/${encodeURIComponent(sessao)}/cupom`, { method: "POST", body: JSON.stringify({ codigo }) });
          contar("cupom", r.ok ? { resultado: "aplicado" } : { resultado: "recusado", motivo: r.fora ? "Instabilidade" : r.erro });
          await estado.refetch();
          return r.ok ? null : r.fora ? "Estamos com instabilidade. Tente em alguns minutos." : r.erro;
        }}
        aoTirarCupom={async () => {
          if (!sessao) { setCupomAplicado(""); return; }
          await chamar(`/v1/tentativas/${encodeURIComponent(sessao)}/cupom`, { method: "DELETE" });
          await estado.refetch();
        }}
        cotando={cotando}
        falhaNaCotacao={falhaNaCotacao}
        aoTentarCotacao={() => void cotacao.refetch()}
        aoSeguirSemBeneficios={() => {
          setPreenchido({ ...preenchido, beneficios: [] });
          // Com a tentativa aberta, os beneficios ficaram nela: seguir sem eles e comecar de novo com os mesmos dados.
          if (sessao) setBusca({}, { replace: true });
        }}
        aoConcluir={concluir}
        aoRecomecar={() => setBusca({}, { replace: true })}
        comprador={comprador}
        api={conta.api}
        avisoDaConta={avisoDaConta}
        acoesDaConta={acoesDaConta}
        aoSair={() => void sair()}
        saindo={saindo}
        aoPerderSessao={perdeuASessao}
        aoAtualizarPreco={atualizarPreco}
        aoContar={contar}
      />
    );
  }

  // A faixa da promocao (spec 067) enquanto a pessoa decide: some quando o pagamento ja saiu (PIX gerado, pago) e quando
  // um cupom maior ganhou dela (2.7). A contagem chegando a zero busca o preco de novo.
  const cupomGanhou = descontoDoCupom > 0;
  const faixa = daVitrine && !cupomGanhou && !(pedido && (pedido.status === "PAGO" || pedido.status === "AGUARDANDO")) ? (
    <DestaqueDaPromocao nome={daVitrine.nome} chamada={daVitrine.chamada} tema={daVitrine.tema} desconto={descontoAnunciado(daVitrine, v)} terminaEm={daVitrine.termina_em} aoTerminar={atualizarPreco} />
  ) : undefined;

  return (
    <>
      {casca(
        pedido?.status === "PAGO" ? (
          <section className="mtc-cartao" aria-label="Compra confirmada">{principal}</section>
        ) : (
          <section className="mtc-cartao" aria-labelledby="checkout-pagamento">
            <h2 id="checkout-pagamento">Dados e pagamento</h2>
            {principal}
          </section>
        ),
        pedido?.status === "PAGO" ? undefined : lado,
        faixa,
      )}
      {opcoes && (
        <JanelaDePlataforma
          aberto={janela}
          opcoes={opcoes.map((o) => ({ valor: o.plataforma, rotulo: NOME_DA_PLATAFORMA[o.plataforma], detalhe: detalheDaPlataforma(o) }))}
          valor={marcada}
          aoEscolher={setMarcada}
          aoContinuar={() => { if (marcada) setPlataforma(marcada); setJanela(false); }}
          aoFechar={() => { if (marcada) setPlataforma(marcada); setJanela(false); }}
        />
      )}
    </>
  );
}

/**
 * Cupom de desconto (spec 016). Quem tem o codigo digita e aplica; quando o checkout tem cupom em destaque, a propria
 * pagina oferece ("voce tem um cupom"). Quem calcula o desconto e o servidor: aqui so entra o codigo.
 */
function CupomDoCheckout({ cupom, desconto, erroDoServidor, emDestaque, aoAplicar, aoTirar, valor = formatarReais }: {
  cupom: string;
  desconto: number;
  erroDoServidor: string | null;
  emDestaque: Vitrine["cupom_em_destaque"];
  aoAplicar: (codigo: string) => Promise<string | null>;
  aoTirar: () => Promise<void>;
  /** Como o valor aparece (em dolar na vitrine em dolar, 15/10). */
  valor?: (centavos: number) => string;
}) {
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const aplicar = async (codigo: string) => {
    if (!codigo.trim()) return;
    setEnviando(true);
    setErro(null);
    try {
      setErro(await aoAplicar(codigo.trim().toUpperCase()));
    } finally {
      setEnviando(false);
    }
  };
  if (cupom && desconto > 0) {
    return (
      <SecaoDoCheckout titulo="Cupom de desconto">
        <p style={{ margin: 0 }}>
          <strong className="mtc-mono">{cupom}</strong> aplicado: <strong>- {valor(desconto)}</strong>{" "}
          <button type="button" className="mtc-link" onClick={() => void aoTirar()}>Remover</button>
        </p>
      </SecaoDoCheckout>
    );
  }
  return (
    <SecaoDoCheckout titulo="Cupom de desconto">
      {emDestaque && !cupom && (
        <Aviso tipo="sucesso">
          Você tem um cupom de <strong>{valor(emDestaque.desconto_centavos)}</strong> de desconto ({emDestaque.codigo}).{" "}
          <button type="button" className="mtc-link" onClick={() => void aplicar(emDestaque.codigo)}>Usar este cupom</button>
        </Aviso>
      )}
      <div className="mtc-linha" style={{ gap: "var(--e-3)", alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <Campo rotulo="Tem um cupom?" valor={texto} aoMudar={(v) => { setTexto(v.toUpperCase()); setErro(null); }} mono placeholder="Digite o código" erro={erro ?? erroDoServidor ?? undefined} />
        </div>
        <Botao type="button" variante="secundario" carregando={enviando} disabled={!texto.trim()} onClick={() => void aplicar(texto)}>Aplicar</Botao>
      </div>
    </SecaoDoCheckout>
  );
}

/**
 * Tudo numa pagina so, como no legado: resumo, meio, dados do comprador (so antes da tentativa), cartao, regulamento e
 * o botao. O botao abre a tentativa (se ainda nao existe) e paga em seguida. Sem sessao (spec 045), no lugar do meio,
 * dos dados e do botao fica o "Entre ou crie sua conta pra comprar"; o resumo, os beneficios e o cupom continuam.
 */
function Formulario({ vitrine, oferta, sessao, regulamento, plataforma, precisaDePlataforma, aviso, linhaDaPlataforma, aoTrocarPlataforma, preenchido, aoPreencher, escolhidos, cupom, cupomDesconto, cupomErro, promocao, cupomEmDestaque, aoAplicarCupom, aoTirarCupom, cotando, falhaNaCotacao, aoTentarCotacao, aoSeguirSemBeneficios, aoConcluir, aoRecomecar, comprador, api, avisoDaConta, acoesDaConta, aoSair, saindo, aoPerderSessao, aoAtualizarPreco, aoContar }: {
  vitrine: Vitrine;
  oferta: Vitrine;
  sessao: string;
  /** So conta quando o checkout tem termos: as caixas do nucleo, a caixa da marca ou o aviso de que nao da pra vender. */
  regulamento: RegulamentoDaPagina;
  plataforma: Plataforma | null;
  precisaDePlataforma: boolean;
  aviso: string | null;
  linhaDaPlataforma: { rotulo: string; valor: string } | null;
  aoTrocarPlataforma?: () => void;
  preenchido: Preenchido;
  aoPreencher: (p: Preenchido) => void;
  /** Beneficios marcados (antes da tentativa) ou os que ficaram nela (depois). */
  escolhidos: string[];
  /** Cupom aplicado (spec 016), o desconto que ele deu e o erro quando o servidor recusou o codigo. */
  cupom: string;
  cupomDesconto: number;
  cupomErro: string | null;
  /** A promocao que vale nesta escolha (spec 067): vira a linha "Promocao <nome>" do resumo, com o desconto do servidor. */
  promocao: Pick<PromocaoNaVitrine, "nome" | "desconto_centavos"> | null;
  cupomEmDestaque: Vitrine["cupom_em_destaque"];
  /** Devolve a frase do erro quando o codigo nao serve, ou nulo quando aplicou. */
  aoAplicarCupom: (codigo: string) => Promise<string | null>;
  aoTirarCupom: () => Promise<void>;
  /** A cotacao do servidor ainda nao voltou: o total mostrado pode estar velho, entao nao paga. */
  cotando: boolean;
  /** A cotacao falhou: a tela explica e oferece tentar de novo ou seguir sem os beneficios. */
  falhaNaCotacao: { erro: string | null; fora: boolean } | null;
  aoTentarCotacao: () => void;
  aoSeguirSemBeneficios: () => void;
  /** Com a tentativa aberta (e paga ou nao): a pagina busca a situacao e guarda a sessao no link. */
  aoConcluir: (sessao: string) => Promise<void>;
  aoRecomecar: () => void;
  /** Quem compra (spec 045): sem sessao, o bloco de entrar ou criar conta; logado, os dados da conta travados. */
  comprador: Comprador;
  /** A chamada autenticada da sessao: a tentativa vai com o token da area. */
  api: <R>(caminho: string, init?: RequestInit) => Promise<R>;
  avisoDaConta: string | null;
  acoesDaConta: ReturnType<typeof useContaNaCompra>;
  aoSair: () => void;
  saindo: boolean;
  aoPerderSessao: () => Promise<void>;
  /** Busca de novo o preco do checkout e a cotacao (o servico recusou porque o valor mudou). */
  aoAtualizarPreco: () => void;
  /** Analise (spec 062): conta um passo da visita. */
  aoContar: (nome: Parameters<typeof contarNoCheckout>[1], dados?: Parameters<typeof contarNoCheckout>[2]) => void;
}) {
  const meio: Meio = preenchido.meio ?? ((oferta.meios[0] as Meio | undefined) ?? "pix");
  const { aceite, aceites, cartao } = preenchido;
  const logado = comprador.estado === "logado" ? comprador : null;
  // O celular vem preenchido com o da conta; o que a pessoa digitar vale pra esta compra.
  const telefone = preenchido.telefone ?? (logado?.telefone ? formatarTelefone(logado.telefone) : "");
  const setMeio = (m: Meio) => aoPreencher({ ...preenchido, meio: m });
  const setAceite = (a: boolean) => aoPreencher({ ...preenchido, aceite: a });
  const setAceites = (a: string[]) => aoPreencher({ ...preenchido, aceites: a });
  // So paga com TODAS as caixas marcadas (uma por documento); na caixa unica da marca, com ela marcada.
  const aceitou = !vitrine.termos || (regulamento.estado === "nucleo" ? regulamento.documentos.every((d) => aceites.includes(d.chave)) : regulamento.estado === "marca" && aceite);
  const setCartao = (c: DadosDoCartao) => aoPreencher({ ...preenchido, cartao: c });
  const marcar = (id: string, v: boolean) => aoPreencher({ ...preenchido, beneficios: v ? [...preenchido.beneficios, id] : preenchido.beneficios.filter((x) => x !== id) });
  const marcados = vitrine.beneficios.filter((b) => escolhidos.includes(b.id));
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [fora, setFora] = useState(false);
  const temSessao = Boolean(sessao);
  // Com a tentativa aberta (`?s=`) a pessoa ja e conhecida: paga sem precisar da sessao (refresh, outro aparelho).
  const podePagar = temSessao || Boolean(logado);
  const parcela = meio === "cartao" ? parcelaEscolhida(cartao, oferta.parcelas) : null;
  const total = parcela ? parcela.total_centavos : oferta.preco_centavos;
  const dinheiro = dinheiroDa(vitrine);
  const tel = digitos(telefone);
  // Sessao assistida (spec 012) so olha: o servico recusa a compra, e a tela nem oferece.
  const dadosProntos = temSessao || (Boolean(logado) && !logado?.assistido && tel.length >= 10 && aceitou);
  const pronto = dadosProntos && !cotando && !falhaNaCotacao && (!precisaDePlataforma || Boolean(plataforma)) && (meio === "pix" || cartaoPronto(cartao));

  // Analise (spec 062): o aceite do regulamento (uma vez) e cada erro que a pessoa viu nesta parte da pagina.
  const aceiteContado = useRef(false);
  useEffect(() => {
    if (!aceitou || !vitrine.termos || temSessao || aceiteContado.current) return;
    aceiteContado.current = true;
    aoContar("regulamento_aceito");
  }, [aceitou]);
  useEffect(() => { if (erro) aoContar("erro", { etapa: "pagar", mensagem: erro }); }, [erro]);
  useEffect(() => { if (fora) aoContar("erro", { etapa: "pagar", mensagem: "Instabilidade no pagamento" }); }, [fora]);
  useEffect(() => { if (falhaNaCotacao) aoContar("erro", { etapa: "cotacao", mensagem: falhaNaCotacao.erro ?? "Instabilidade na cotação" }); }, [falhaNaCotacao?.erro, falhaNaCotacao?.fora]);
  useEffect(() => { if (vitrine.termos && regulamento.estado === "sem-regulamento") aoContar("erro", { etapa: "pagina", mensagem: "Regulamento do plano não publicado" }); }, [regulamento.estado]);

  const pagar = async (ev: FormEvent) => {
    ev.preventDefault();
    setErro(null);
    setFora(false);
    setEnviando(true);
    try {
      // Cartao: tokeniza antes (o numero nunca passa pelo nosso servidor).
      let corpoCartao = null;
      if (meio === "cartao") {
        const c = await corpoDoCartao(oferta.chave_publica, cartao);
        if (!c.ok) return setErro(c.erro);
        // O total que esta na tela: se o preco mudou enquanto a pessoa preenchia, o servico recusa em vez de cobrar outro.
        corpoCartao = { ...c.corpo, total_centavos: total };
      }
      let s = sessao;
      if (!s) {
        // A versao que a pagina mostrou e as caixas marcadas (spec 042); na caixa da marca, o rotulo antigo.
        const doRegulamento = !vitrine.termos ? {} : regulamento.estado === "nucleo" ? { aceite_regulamento: { grupo: regulamento.grupo, documentos: regulamento.documentos.map((d) => d.chave) } } : aceite ? { aceite_termos_versao: vitrine.termos.versao } : {};
        // Nome, CPF e e-mail nao vao: o servico pega da conta da sessao (spec 045, 3.4 e 3.8). O celular vai como digitado.
        // A visita vai junto (spec 062): liga a compra aos passos da pagina e a campanha de onde a pessoa veio.
        // Cupom que a cotacao recusou (esgotou, ou a promocao ja da mais, spec 067) nao vai: a compra sai pelo total da tela.
        const corpo: NovaTentativa = { telefone, visitante: visitante(), origem: origemDaVisita(vitrine.token), ...doRegulamento, ...(precisaDePlataforma && plataforma ? { plataforma } : {}), beneficios: preenchido.beneficios, ...(cupom && !cupomErro ? { cupom } : {}) };
        const r = await chamarComSessao<{ sessao: string }>(api, `/v1/checkouts/${encodeURIComponent(vitrine.token)}/tentativas`, { method: "POST", body: JSON.stringify(corpo) });
        if (!r.ok) {
          // A sessao venceu (ou saiu em outra aba): volta pro entrar sem perder nada do que foi escolhido.
          if (r.status === 401) return void (await aoPerderSessao());
          return r.fora ? setFora(true) : setErro(r.erro);
        }
        s = r.dados.sessao;
      }
      const r = meio === "pix"
        ? await chamar(`/v1/tentativas/${encodeURIComponent(s)}/pix`, { method: "POST" })
        : await chamar(`/v1/tentativas/${encodeURIComponent(s)}/cartao`, { method: "POST", body: JSON.stringify(corpoCartao) });
      if (!r.ok) {
        if (r.fora) setFora(true);
        else setErro(r.erro);
        // O preco pode ter mudado (checkout editado): a pagina busca de novo e o resumo mostra o valor certo.
        if (!r.fora) aoAtualizarPreco();
      }
      // Mesmo com erro a tentativa ja existe: fica no link, e a proxima tentativa de pagar usa a mesma.
      await aoConcluir(s);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Pilha espaco={5}>
    <form onSubmit={pagar} noValidate>
      <Pilha espaco={5}>
        <ResumoDoPedido
          linhas={[{ rotulo: vitrine.nome, valor: dinheiro.valor(vitrine.preco_centavos) }, ...(linhaDaPlataforma ? [linhaDaPlataforma] : []), ...marcados.map((b) => ({ rotulo: b.nome, valor: b.preco_centavos ? dinheiro.valor(b.preco_centavos) : "Grátis" })), ...(cupom && cupomDesconto > 0 ? [{ rotulo: `Desconto (${cupom})`, valor: `- ${dinheiro.valor(cupomDesconto)}`, desconto: true }] : []), ...(promocao && promocao.desconto_centavos > 0 ? [{ rotulo: `Promoção ${promocao.nome}`, valor: `- ${dinheiro.valor(promocao.desconto_centavos)}`, desconto: true }] : []), ...(parcela && parcela.juros_centavos > 0 ? [{ rotulo: `Juros do parcelamento em ${parcela.parcelas}x`, valor: dinheiro.valor(parcela.juros_centavos) }] : [])]}
          total={{ rotulo: meio === "cartao" && parcela && parcela.parcelas > 1 ? `Total no cartão em ${parcela.parcelas}x` : "Total", centavos: total, emDolar: dinheiro.emDolar(total) }}
          acao={aoTrocarPlataforma ? <button type="button" className="mtc-link" onClick={aoTrocarPlataforma}>{plataforma ? "Alterar plataforma" : "Escolher plataforma"}</button> : undefined}
        />
        {aviso && <Aviso tipo="erro">{aviso}</Aviso>}
        {/* Beneficios da oferta (spec 013, item 2): a pessoa marca antes de enviar os dados; depois, ficam fixos. */}
        {vitrine.beneficios.length > 0 && (
          <SecaoDoCheckout titulo={temSessao ? "Incluído na sua compra" : "Adicione à sua compra"}>
            {temSessao ? (
              marcados.length ? marcados.map((b) => <p key={b.id} style={{ margin: 0 }}>{b.nome} <span className="mtc-texto-2">({b.preco_centavos ? dinheiro.valor(b.preco_centavos) : "grátis"}{b.duracao_dias ? `, vale ${b.duracao_dias} dias` : ""})</span></p>) : <p className="mtc-texto-2" style={{ margin: 0 }}>Nenhum benefício escolhido.</p>
            ) : (
              vitrine.beneficios.map((b) => (
                <CampoCaixa key={b.id} marcado={preenchido.beneficios.includes(b.id)} aoMudar={(v) => marcar(b.id, v)}>
                  <strong>{b.nome}</strong> <span className="mtc-texto-2">({b.preco_centavos ? `+ ${dinheiro.valor(b.preco_centavos)}` : "grátis"}{b.duracao_dias ? `, vale ${b.duracao_dias} dias` : ""})</span>
                  {b.descricao && <span className="mtc-campo__dica" style={{ display: "block" }}>{b.descricao}</span>}
                </CampoCaixa>
              ))
            )}
            {falhaNaCotacao && (
              <Pilha espaco={2}>
                {falhaNaCotacao.fora ? <Instabilidade /> : <Aviso tipo="erro">{falhaNaCotacao.erro}</Aviso>}
                <div className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "wrap" }}>
                  {falhaNaCotacao.fora && <Botao type="button" variante="secundario" tamanho="pequeno" onClick={aoTentarCotacao}>Tentar de novo</Botao>}
                  <Botao type="button" variante="fantasma" tamanho="pequeno" onClick={aoSeguirSemBeneficios}>Seguir sem os benefícios</Botao>
                </div>
              </Pilha>
            )}
          </SecaoDoCheckout>
        )}
        {/* Cupom de desconto (spec 016): quem tem o codigo digita; o destaque a propria pagina oferece. Checkout sem nenhum
            cupom valendo nem mostra o campo (Vitor, 14/10); um ja aplicado continua aparecendo pra poder tirar. */}
        {(vitrine.aceita_cupom || Boolean(cupom)) && (
          <CupomDoCheckout cupom={cupom} desconto={cupomDesconto} erroDoServidor={cupomErro} emDestaque={cupomEmDestaque} aoAplicar={aoAplicarCupom} aoTirar={aoTirarCupom} valor={dinheiro.valor} />
        )}
        {podePagar && <>
        {oferta.meios.length > 1 && (
          <EscolhaDeMeio<Meio> valor={meio} aoMudar={setMeio} opcoes={oferta.meios.map((x) => ({ valor: x as Meio, rotulo: x === "pix" ? "PIX" : "Cartão de crédito", icone: x === "pix" ? <IconePix tamanho={18} /> : <IconeCartao tamanho={18} /> }))} />
        )}
        {temSessao ? (
          <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>
            Seus dados já foram enviados. <button type="button" className="mtc-link" onClick={aoRecomecar}>Usar outros dados</button>
          </p>
        ) : logado && (
          <CompradorDaConta
            nome={logado.nome}
            cpf={logado.cpf}
            email={logado.email}
            nota={`${vitrine.sem_assinatura ? "O pagamento" : "O plano"} fica neste CPF. Se o cartão for de outra pessoa, tudo bem.`}
            aoSair={logado.assistido ? undefined : aoSair}
            saindo={saindo}
          >
            <Campo rotulo="Celular com DDD" valor={telefone} aoMudar={(v) => aoPreencher({ ...preenchido, telefone: formatarTelefone(v) })} inputMode="tel" autoComplete="tel" placeholder="(11) 98765-4321" required />
          </CompradorDaConta>
        )}
        {meio === "cartao" && (
          <SecaoDoCheckout titulo="Dados do cartão">
            <CamposDoCartao valor={cartao} aoMudar={setCartao} parcelas={oferta.parcelas} formatar={dinheiro.valor} />
          </SecaoDoCheckout>
        )}
        {!temSessao && (vitrine.termos || config().PRIVACIDADE_URL) && (
          <SecaoDoCheckout titulo="Documentos da compra" icone={<IconeEscudo tamanho={16} />}>
            {vitrine.termos && (regulamento.estado === "carregando" ? (
              <Carregando compacto texto="Buscando o regulamento" />
            ) : regulamento.estado === "nucleo" ? (
              <AceiteDosDocumentos documentos={regulamento.documentos} marcados={aceites} aoMudar={setAceites} />
            ) : regulamento.estado === "sem-regulamento" ? (
              <Aviso tipo="erro">O regulamento deste plano ainda não foi publicado, então a compra não pode seguir agora. Fale com a equipe{marca().whatsapp ? <> pelo <a href={marca().whatsapp} target="_blank" rel="noreferrer">WhatsApp</a></> : null}.</Aviso>
            ) : (
              <CampoCaixa marcado={aceite} aoMudar={setAceite}>
                Li e aceito o <a href={vitrine.termos.url} target="_blank" rel="noreferrer">regulamento</a>.
              </CampoCaixa>
            ))}
            {config().PRIVACIDADE_URL && (
              <p className="mtc-checkout__apos">Seus dados são tratados conforme a <a href={config().PRIVACIDADE_URL} target="_blank" rel="noreferrer">política de privacidade</a>.</p>
            )}
          </SecaoDoCheckout>
        )}
        {vitrine.sem_assinatura ? (
          // O "apos a compra" do ambiente fala do plano (agendar o inicio, preparar a conta): aqui nao vale.
          <p className="mtc-checkout__apos">Após a confirmação, você recebe o comprovante por e-mail. Este pagamento é avulso e não inclui plano.</p>
        ) : (
          // Comprando logado (spec 045), o acesso ja existe: o plano aparece na area e o e-mail leva o comprovante.
          <p className="mtc-checkout__apos">Após a confirmação, o plano aparece na sua área{vitrine.apos_a_compra ? `, ${vitrine.apos_a_compra}` : ""}. O comprovante vai pro seu e-mail.</p>
        )}
        {logado?.assistido && !temSessao && <Aviso tipo="alerta">Sessão assistida: você vê o checkout como o cliente, mas só ele pode comprar, entrando na conta dele.</Aviso>}
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        {fora && <Instabilidade />}
        <Botao type="submit" largo carregando={enviando} disabled={!pronto} icone={meio === "pix" ? <IconePix tamanho={18} /> : <IconeCartao tamanho={18} />}>
          {meio === "pix" ? `Gerar PIX de ${dinheiro.valor(oferta.preco_centavos)}` : `Pagar ${dinheiro.valor(total)}`}
          <IconeSeta tamanho={16} />
        </Botao>
        </>}
      </Pilha>
    </form>
    {/* Fora do <form> da compra: o bloco de conta tem os formularios dele (entrar, criar conta, codigo). */}
    {!podePagar && (comprador.estado === "carregando" ? (
      <Carregando compacto texto="Conferindo sua sessão" />
    ) : (
      <ContaNaCompra
        aviso={avisoDaConta}
        rodape={config().PRIVACIDADE_URL ? <>Seus dados são tratados conforme a <a href={config().PRIVACIDADE_URL} target="_blank" rel="noreferrer">política de privacidade</a>.</> : null}
        {...acoesDaConta}
      />
    ))}
    </Pilha>
  );
}
